import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Grid,
  LinearProgress,
  Stack,
  Switch,
  TextField,
  Typography,
  MenuItem,
  IconButton,
  Paper,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DashboardLayout from '../layouts/DashboardLayout';
import { listTimetables, createTimetable, generateSessions } from '../api/timetables';
import { listSubjects } from '../api/subjects';
import { listUsers } from '../api/users';
import { listGradeSections } from '../api/gradeSections';

const TimetablesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [seedNote, setSeedNote] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [educators, setEducators] = useState([]);
  const [sections, setSections] = useState([]);
  const [periodForm, setPeriodForm] = useState({ start: '08:00', end: '08:45' });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [form, setForm] = useState({
    name: '',
    year: new Date().getFullYear(),
    grade: '',
    sectionId: '',
    slots: [],
    startDate: '',
    endDate: '',
  });

  const load = async () => {
    try {
      setLoading(true);
      const [data, subjectData, educatorData, sectionData] = await Promise.all([
        listTimetables(),
        listSubjects(),
        listUsers({ role: 'educator' }),
        listGradeSections({ isActive: true }),
      ]);
 setTemplates(Array.isArray(data) ? data : []);
 setSubjects(Array.isArray(subjectData) ? subjectData : []);
 setEducators(Array.isArray(educatorData) ? educatorData : []);
 setSections(Array.isArray(sectionData) ? sectionData : []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load timetables');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const buildSeedSlots = () => {
    const times = [
      { start: '08:00', end: '08:45' },
      { start: '09:00', end: '09:45' },
      { start: '10:00', end: '10:45' },
      { start: '11:00', end: '11:45' },
      { start: '12:00', end: '12:45' },
    ];
    const slots = [];
    const liveDays = [1, 2, 3, 4];
    const friday = 5;

    liveDays.forEach((day) => {
      times.forEach((t) => {
        slots.push({
          dayOfWeek: day,
          startTime: t.start,
          endTime: t.end,
          subjectId: '',
          educatorId: '',
          sessionType: 'live',
          title: null,
          joinUrl: null,
        });
      });
    });

    times.forEach((t) => {
      slots.push({
        dayOfWeek: friday,
        startTime: t.start,
        endTime: t.end,
        subjectId: '',
        educatorId: '',
        sessionType: 'sba',
        title: 'SBA/Clubs',
        joinUrl: null,
      });
    });

    return slots;
  };

  const handleSeedGrid = () => {
    const seeded = buildSeedSlots();
    setForm((prev) => ({ ...prev, slots: seeded }));
    setSeedNote('Seeded default grid. Assign subjects and educators to each slot before saving.');
  };

  const subjectOptions = useMemo(() => {
    if (!form.grade) return subjects;
    return subjects.filter((s) => Number(s.grade) === Number(form.grade));
  }, [subjects, form.grade]);

  const educatorOptions = useMemo(() => educators.map((u) => ({
    value: u._id,
    label: `${u.name || ''} ${u.surname || ''}`.trim() || u.email,
  })), [educators]);

  const sectionOptions = useMemo(() => {
    if (!form.grade) return sections;
    return sections.filter((s) => Number(s.grade) === Number(form.grade));
  }, [sections, form.grade]);

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const slotIndexByKey = useMemo(() => {
    const map = new Map();
    form.slots.forEach((slot, idx) => {
      const key = `${slot.dayOfWeek}-${slot.startTime}-${slot.endTime}`;
      map.set(key, idx);
    });
    return map;
  }, [form.slots]);

  const timeRows = useMemo(() => {
    const uniq = new Map();
    form.slots.forEach((slot) => {
      const key = `${slot.startTime}-${slot.endTime}`;
      uniq.set(key, { startTime: slot.startTime, endTime: slot.endTime });
    });
    return Array.from(uniq.values()).sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [form.slots]);

  const updateSlot = (index, updates) => {
    const next = [...form.slots];
    next[index] = { ...next[index], ...updates };
    setForm((prev) => ({ ...prev, slots: next }));
  };

  const handleAddPeriodRow = () => {
    if (!periodForm.start || !periodForm.end) return;
    const next = [...form.slots];
    [1, 2, 3, 4, 5].forEach((day) => {
      const key = `${day}-${periodForm.start}-${periodForm.end}`;
      if (slotIndexByKey.has(key)) return;
      next.push({
        dayOfWeek: day,
        startTime: periodForm.start,
        endTime: periodForm.end,
        subjectId: '',
        educatorId: '',
 sessionType: day === 5 ? 'sba' : 'live',
 title: day === 5 ? 'SBA/Clubs' : null,
        joinUrl: null,
      });
    });
    setForm((prev) => ({ ...prev, slots: next }));
  };

  const handleCreate = async (evt) => {
    evt.preventDefault();
    setLoading(true);
    setError(null);
    setSeedNote('');
    try {
      await createTimetable({
        name: form.name,
        year: Number(form.year),
        grade: Number(form.grade),
        sectionId: form.sectionId || undefined,
        slots: form.slots,
      });
      setForm((prev) => ({ ...prev, name: '', slots: [] }));
      await load();
    } catch (err) {
      setError(err?.message || 'Failed to create timetable');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (id) => {
    if (!form.startDate || !form.endDate) {
      setError('Provide start and end dates to generate sessions.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await generateSessions(id, { startDate: form.startDate, endDate: form.endDate });
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to generate sessions');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title="Timetable Templates">
      <Box>
        <Stack spacing={2.5}>
          {loading && <LinearProgress />}
          {error && <Alert severity="error">{error}</Alert>}

          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardHeader title="Create template" />
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack spacing={2} component="form" onSubmit={handleCreate}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Name"
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    required
                  />
                  <TextField
                    label="Year"
                    type="number"
                    value={form.year}
                    onChange={(e) => setForm((prev) => ({ ...prev, year: e.target.value }))}
                  />
                  <TextField
                    label="Grade"
                    type="number"
                    value={form.grade}
                    onChange={(e) => setForm((prev) => ({ ...prev, grade: e.target.value }))}
                    required
                  />
                </Stack>
                {sectionOptions.length > 0 && (
                  <TextField
                    select
                    label="Section"
                    value={form.sectionId}
                    onChange={(e) => setForm((prev) => ({ ...prev, sectionId: e.target.value }))}
                  >
                    <MenuItem value="">All sections</MenuItem>
                    {sectionOptions.map((sec) => (
                      <MenuItem key={sec._id} value={sec._id}>
                        {sec.year}-G{sec.grade}{sec.section}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                <Divider />

                <Stack spacing={1}>
                  <Typography variant="subtitle2">Period grid planner</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Edit subjects and educators in a weekly period grid.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                    <TextField
                      label="Start"
                      type="time"
                      value={periodForm.start}
                      onChange={(e) => setPeriodForm((prev) => ({ ...prev, start: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="End"
                      type="time"
                      value={periodForm.end}
                      onChange={(e) => setPeriodForm((prev) => ({ ...prev, end: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                    <Button variant="outlined" onClick={handleAddPeriodRow}>
                      Add period row (Mon-Fri)
                    </Button>
                  </Stack>

                  <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 900 }}>
                      <Stack spacing={1} sx={{ p: 2 }}>
                        <Stack direction="row" spacing={2}>
                          <Box sx={{ width: 120 }}>
                            <Typography variant="caption" color="text.secondary">Time</Typography>
                          </Box>
                          {[1, 2, 3, 4, 5].map((day) => (
                            <Box key={day} sx={{ flex: 1 }}>
                              <Typography variant="caption" color="text.secondary">{dayLabels[day]}</Typography>
                            </Box>
                          ))}
                        </Stack>
                        {timeRows.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            No periods yet. Seed the grid or add a period row.
                          </Typography>
                        )}
                        {timeRows.map((row) => (
                          <Stack key={`${row.startTime}-${row.endTime}`} direction="row" spacing={2} alignItems="flex-start">
                            <Box sx={{ width: 120 }}>
                              <Typography variant="body2">
                                {row.startTime} - {row.endTime}
                              </Typography>
                            </Box>
                            {[1, 2, 3, 4, 5].map((day) => {
                              const key = `${day}-${row.startTime}-${row.endTime}`;
                              const idx = slotIndexByKey.get(key);
 const slot = typeof idx === 'number' ? form.slots[idx] : null;
                              return (
                                <Box key={key} sx={{ flex: 1 }}>
                                  {slot ? (
                                    <Stack spacing={1}>
                                      <TextField
                                        select
                                        size="small"
                                        label="Subject"
                                        value={slot.subjectId || ''}
                                        onChange={(e) => updateSlot(idx, { subjectId: e.target.value })}
                                        fullWidth
                                      >
                                        <MenuItem value="">Unassigned</MenuItem>
                                        {subjectOptions.map((subject) => (
                                          <MenuItem key={subject._id} value={subject._id}>
                                            {subject.name} (G{subject.grade})
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                      <TextField
                                        select
                                        size="small"
                                        label="Educator"
                                        value={slot.educatorId || ''}
                                        onChange={(e) => updateSlot(idx, { educatorId: e.target.value })}
                                        fullWidth
                                      >
                                        <MenuItem value="">Unassigned</MenuItem>
                                        {educatorOptions.map((educator) => (
                                          <MenuItem key={educator.value} value={educator.value}>
                                            {educator.label}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                      <TextField
                                        select
                                        size="small"
                                        label="Type"
                                        value={slot.sessionType || 'live'}
                                        onChange={(e) => updateSlot(idx, {
                                          sessionType: e.target.value,
                                          title: e.target.value === 'sba' ? 'SBA/Clubs' : slot.title,
                                        })}
                                        fullWidth
                                      >
                                        <MenuItem value="live">Live</MenuItem>
                                        <MenuItem value="sba">SBA</MenuItem>
                                        <MenuItem value="club">Club</MenuItem>
                                        <MenuItem value="catchup">Catch-up</MenuItem>
                                      </TextField>
                                    </Stack>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">
                                      -
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  </Paper>
                </Stack>

                <Stack spacing={1}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="subtitle2">Advanced slot editor</Typography>
                    <FormControlLabel
                      control={<Switch checked={showAdvanced} onChange={(e) => setShowAdvanced(e.target.checked)} />}
                      label={showAdvanced ? 'Shown' : 'Hidden'}
                    />
                  </Stack>
                  {showAdvanced && (
                    <>
                      {seedNote && (
                        <Typography variant="body2" color="text.secondary">
                          {seedNote}
                        </Typography>
                      )}
                      {form.slots.map((slot, idx) => (
                        <Stack key={idx} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                          <TextField
                            select
                            label="Day"
                            value={slot.dayOfWeek}
                            onChange={(e) => {
                              const next = [...form.slots];
                              next[idx].dayOfWeek = Number(e.target.value);
                              setForm((prev) => ({ ...prev, slots: next }));
                            }}
                            sx={{ minWidth: 120 }}
                          >
                            {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                              <MenuItem key={d} value={d}>{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]}</MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            label="Start"
                            type="time"
                            value={slot.startTime}
                            onChange={(e) => {
                              const next = [...form.slots];
                              next[idx].startTime = e.target.value;
                              setForm((prev) => ({ ...prev, slots: next }));
                            }}
                          />
                          <TextField
                            label="End"
                            type="time"
                            value={slot.endTime}
                            onChange={(e) => {
                              const next = [...form.slots];
                              next[idx].endTime = e.target.value;
                              setForm((prev) => ({ ...prev, slots: next }));
                            }}
                          />
                          <TextField
                            select
                            label="Subject"
                            value={slot.subjectId}
                            onChange={(e) => {
                              const next = [...form.slots];
                              next[idx].subjectId = e.target.value;
                              setForm((prev) => ({ ...prev, slots: next }));
                            }}
                            sx={{ minWidth: 180 }}
                          >
                            <MenuItem value="">Unassigned</MenuItem>
                            {subjectOptions.map((subject) => (
                              <MenuItem key={subject._id} value={subject._id}>
                                {subject.name} (G{subject.grade})
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            select
                            label="Educator"
                            value={slot.educatorId}
                            onChange={(e) => {
                              const next = [...form.slots];
                              next[idx].educatorId = e.target.value;
                              setForm((prev) => ({ ...prev, slots: next }));
                            }}
                            sx={{ minWidth: 180 }}
                          >
                            <MenuItem value="">Unassigned</MenuItem>
                            {educatorOptions.map((educator) => (
                              <MenuItem key={educator.value} value={educator.value}>
                                {educator.label}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            select
                            label="Type"
                            value={slot.sessionType || 'live'}
                            onChange={(e) => {
                              const next = [...form.slots];
                              next[idx].sessionType = e.target.value;
                              setForm((prev) => ({ ...prev, slots: next }));
                            }}
                            sx={{ minWidth: 140 }}
                          >
                            <MenuItem value="live">Live</MenuItem>
                            <MenuItem value="sba">SBA</MenuItem>
                            <MenuItem value="club">Club</MenuItem>
                            <MenuItem value="catchup">Catch-up</MenuItem>
                          </TextField>
                          <IconButton color="error" onClick={() => {
                            const next = form.slots.filter((_, i) => i !== idx);
                            setForm((prev) => ({ ...prev, slots: next }));
                          }}>
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      ))}
                      <Button variant="outlined" onClick={() => setForm((prev) => ({ ...prev, slots: [...prev.slots, { dayOfWeek: 1, startTime: '08:00', endTime: '08:45', subjectId: '', educatorId: '', sessionType: 'live' }] }))}>
                        Add slot
                      </Button>
                      <Button variant="outlined" onClick={handleSeedGrid}>
                        Seed weekly grid (Mon-Thu live, Fri SBA/Clubs)
                      </Button>
                    </>
                  )}
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Generate start date"
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm((prev) => ({ ...prev, startDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Generate end date"
                    type="date"
                    value={form.endDate}
                    onChange={(e) => setForm((prev) => ({ ...prev, endDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
                <Stack direction="row" spacing={2}>
                  <Button type="submit" variant="contained">Create template</Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Grid container spacing={2}>
            {templates.map((tpl) => (
                <Grid item xs={12} md={6} key={tpl._id}>
                  <Card elevation={1} sx={{ borderRadius: 3 }}>
                    <CardHeader
                      title={tpl.name}
 subheader={`Year ${tpl.year} | Grade ${tpl.grade}${tpl.sectionId ? ` | Section ${tpl.sectionId.section}` : ''}`}
                    />
                    <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Slots: {tpl.slots?.length || 0}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button variant="outlined" onClick={() => handleGenerate(tpl._id)} disabled={loading}>
                          Generate sessions
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
            ))}
          </Grid>
        </Stack>
      </Box>
    </DashboardLayout>
  );
};

export default TimetablesPage;
